import { useMemo, useState } from 'react';

import { Box, Button, Stack, Tooltip } from '@mui/joy';
import { FaArrowLeft } from 'react-icons/fa';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import InsightsHubBreadcrumbs from '../pages/insightsHub/components/Breadcrumbs';
import CategoryQuickSearches from '../pages/insightsHub/components/CategoryQuickSearches';
import { insightsHubCategoriesConfig } from '../pages/insightsHub/constants';
import useInsightsHubBreadcrumbs from '../pages/insightsHub/useInsightsHubBreadcrumbs';
import { UPGRADE_SOURCE } from '../utils/constants/upgradeSources';
import { dateFormat } from '../utils/functions/date';
import useInsightsHubReturn from '../utils/hooks/useInsightsHubReturn';
import { usePermissions } from '../utils/hooks/usePermissions';

import { AskAQuestionCard } from './askAQuestion/AskAQuestionCard';
import InsightsHubSearch from './InsightsHubSearch';
import Paywall from './Paywall';
import RenderIfHasPermission from './RenderIfHasPermission';
import { Segment, SegmentedControl } from './SegmentedControl';

const HEADERLESS_PATHS = [
  '/research-hub/corsia',
  '/research-hub/research-updates',
  '/research-hub/market-updates',
];
const HEADERLESS_PREFIXES = ['/research-hub/market-update/', '/research-hub/research_update/'];

export default function InsightsHubLayout() {
  const { hasPermission } = usePermissions();
  const hasPermissionForResearchHub = hasPermission('research_hub:research_list');
  const hasSubscriberOnlyAccess = hasPermission('research_hub:subscriber_only');
  const { category, slug } = useParams();
  const { pathname } = useLocation();
  const isResearchUpdate = useMemo(() => pathname.includes('/research_update'), [pathname]);
  const isSearchPage = useMemo(() => pathname.includes('/research-hub/search'), [pathname]);
  const navigate = useNavigate();
  const [showBackButton, setShowBackButton] = useState(true);
  const [lastPathname, setLastPathname] = useState(pathname);
  const [breadcrumbTitle, setBreadcrumbTitle] = useState<string | undefined>();
  const breadcrumbs = useInsightsHubBreadcrumbs(breadcrumbTitle);

  // Pages that render their own full-width header. Derived from the route so
  // the layout header never paints for a frame before the child's effect hides
  // it — the child's hideLayoutNavigation() runs after the first paint.
  const routeOwnsHeader = useMemo(() => {
    const path = pathname.replace(/\/+$/, '');
    return (
      HEADERLESS_PATHS.includes(path) ||
      HEADERLESS_PREFIXES.some((prefix) => path.startsWith(prefix))
    );
  }, [pathname]);

  // Reset on pathname, not slug: sibling routes (/corsia, /research-updates,
  // /market-updates) all have an undefined slug, so returning to /research-hub
  // from one of them never changed the key and the header stayed hidden.
  // Done during render rather than in an effect because child effects run
  // before the parent's — an effect here would undo hideLayoutNavigation().
  if (pathname !== lastPathname) {
    setLastPathname(pathname);
    setShowBackButton(true);
    setBreadcrumbTitle(undefined);
  }

  const handleNavItem = (key: string) => {
    navigate(`/research-hub/${key}`);
  };

  const handleReturn = useInsightsHubReturn();

  const hideLayoutNavigation = () => {
    setShowBackButton(false);
  };

  const showLayoutNavigation = () => {
    setShowBackButton(true);
  };

  const insightsHubCategoriesConfigWithPermissions = useMemo(() => {
    if (!hasSubscriberOnlyAccess) {
      return insightsHubCategoriesConfig?.map((category) => {
        const { key } = category;
        if (key === 'research-updates') {
          return { ...category, showLocked: true };
        } else {
          return category;
        }
      });
    }
    return insightsHubCategoriesConfig;
  }, [insightsHubCategoriesConfig, hasSubscriberOnlyAccess]);

  return (
    <>
      <Paywall
        isLoadingComplete={true}
        showPaywall={!hasPermissionForResearchHub}
        headingText='Access Exclusive Insights in the Research Hub'
        bodyText='Upgrade your subscription to explore Calyx Global’s curated collection of expert blog posts, in-depth resources, and the latest research updates on carbon markets and climate action.'
      />
      {breadcrumbs.length > 0 && <InsightsHubBreadcrumbs items={breadcrumbs} />}
      <Box paddingBottom={12} maxWidth={'1472px'} margin={'auto'} paddingTop={3} paddingX={2}>
        {showBackButton && !routeOwnsHeader ? (
          !!slug ? (
            <Box maxWidth={'850px'} margin={'auto'} mb={1}>
              <Stack gap={2}>
                <RenderIfHasPermission permission='research_hub:search'>
                  <InsightsHubSearch />
                </RenderIfHasPermission>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                  <Button
                    variant='outlined'
                    size='sm'
                    startDecorator={<FaArrowLeft />}
                    onClick={handleReturn}
                  >
                    Return
                  </Button>
                  {isResearchUpdate && (
                    <AskAQuestionCard
                      prefill={`About: ${dateFormat(slug ?? '').monthddyyyy} Research update`}
                      width={323}
                    />
                  )}
                </Stack>
              </Stack>
            </Box>
          ) : !category ? (
            <Stack alignItems={'center'} gap={2} width={'100%'} marginBottom={3}>
              <Box width={'100%'} maxWidth={856}>
                <RenderIfHasPermission permission='research_hub:search'>
                  <InsightsHubSearch />
                </RenderIfHasPermission>
              </Box>
              {!isSearchPage && <CategoryQuickSearches />}
            </Stack>
          ) : (
            <Box
              alignItems={'center'}
              gap={{ xs: 2, lg: 4 }}
              width={'100%'}
              marginBottom={3}
              justifyContent={'space-between'}
              gridTemplateColumns={{ xs: '1fr', lg: '5fr 7fr' }}
              display={'grid'}
            >
              <Box>
                <RenderIfHasPermission permission='research_hub:search'>
                  <InsightsHubSearch />
                </RenderIfHasPermission>
              </Box>
              <Box maxWidth={'100vw'} width={{ xs: '100%', lg: '810px' }} overflow={'auto'}>
                <SegmentedControl sx={{ width: 'max-content' }}>
                  {insightsHubCategoriesConfigWithPermissions
                    .sort((a, b) => a?.orderIndex - b?.orderIndex)
                    .map(({ key, label, showLocked }) => {
                      const isSelected = category === key;
                      return (
                        <Tooltip
                          title={showLocked ? 'Upgrade for access' : ''}
                          key={key}
                          placement='top'
                        >
                          <Segment
                            selected={isSelected}
                            locked={showLocked}
                            onClick={() =>
                              showLocked
                                ? navigate('/upgrade', {
                                    state: { source: UPGRADE_SOURCE.INSIGHTS_HUB_LOCKED_NAV_ITEM },
                                  })
                                : handleNavItem(key)
                            }
                          >
                            {label}
                          </Segment>
                        </Tooltip>
                      );
                    })}
                </SegmentedControl>
              </Box>
            </Box>
          )
        ) : null}
        <Outlet context={{ hideLayoutNavigation, showLayoutNavigation, setBreadcrumbTitle }} />
      </Box>
    </>
  );
}
